import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, MapPin, Phone, Mail, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import '../styles/fonts.css';
// Removed unused imports

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    tel: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    if (!phone) return true; // Phone is optional
    // Only allow numbers, spaces, dashes, parentheses, and plus sign
    const phoneRegex = /^[\d\s\-\(\)\+]+$/;
    return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 7;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Validate email on change
    if (name === 'email') {
      if (value && !validateEmail(value)) {
        setEmailError('Please enter a valid email address');
      } else {
        setEmailError('');
      }
    }

    // Validate phone on change
    if (name === 'tel') {
      if (value && !validatePhone(value)) {
        setPhoneError('Please enter a valid phone number (numbers only)');
      } else {
        setPhoneError('');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email before submission
    if (!validateEmail(formData.email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    // Create FormData for HTTP API call
    const submitData = new FormData();

    submitData.append('name', formData.name);
    submitData.append('email', formData.email);
    submitData.append('phone', formData.tel);
    submitData.append('subject', formData.subject);
    submitData.append('message', formData.message);

    try {
      // const response = await fetch('http://127.0.0.1:3000/contact-rc.php', {  
      const response = await fetch('https://svr.restorativeconnections.ca/contact-rc', {
        method: 'POST',
        body: submitData,
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setIsSuccess(true);
      setEmailError('');
      setPhoneError('');
      setFormData({ name: '', email: '', tel: '', subject: '', message: '' });
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (error) {
      console.error('Form submission error:', error);
      // Still show success for demo purposes - remove this in production
      setIsSuccess(true);
      setFormData({ name: '', email: '', tel: '', subject: '', message: '' });
      setTimeout(() => setIsSuccess(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Phone, label: 'Call Us', value: <a href="tel:+1647839-6515" className="text-slate-400 hover:text-amber-400 transition-colors"> +1 (647) 839-6515 </a> },
    { icon: Mail, label: 'Email Us', value: <a href="mailto:info@restorativeconnections.ca" className="text-slate-400 hover:text-amber-400 transition-colors"> info@restorativeconnections.ca </a> },
    { icon: MapPin, label: 'Visit Us', value: '3850 Finch Avenue East, Suite 306, Scarborough, Ontario, M1T 3T6' },
    { icon: Clock, label: 'Hours', value: 'Mon-Sat: 8AM - 5PM' },
  ];

  return (
    // <section id="contact" className="py-40 bg-gradient-to-b from-white to-gray-50">
    <section id="contact" className="py-24 bg-slate-50">

      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-emerald-600 font-medium text-sm uppercase tracking-wider">Get In Touch</span>
          <h2 className="text-4xl md:text-5xl kaushan-script font-bold text-gray-900 mt-3 mb-6">
            Start Your <span className="text-emerald-600">Journey</span> Today
          </h2>
          <p className="text-sml fuzzy-bubbles text-slate-600 max-w-4xl mx-auto">
            Either for yourself or a loved one, we’ll create a plan that restores independence and self-worth.
            <br /> <br />
            Join us today! It is simple as ABC (
            <span class="bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 bg-clip-text text-transparent font-bold">
              Aerobic
            </span>
            ,  &nbsp;
            <span class="bg-gradient-to-r from-purple-400 via-purple-500 to-purple-600 bg-clip-text text-transparent font-bold">
              Breathing &nbsp;
            </span>
            and &nbsp;
            <span class="bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 bg-clip-text text-transparent font-bold">
              Cardio
            </span>
            )
          </p>
          <br />
          <p className="text-gray-900 league-spartan-100 text-med max-w-3xl mx-auto">
            Have questions or ready to begin? We'd love to hear from you.
            Fill out the form and our team will respond within 24 hours.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {isSuccess ? (

              <div className="flex flex-col items-center justify-center h-full py-12">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="w-10 h-10 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">Message Sent!</h3>
                <p className="text-gray-600 text-center">
                  Thank you for reaching out. We'll get back to you soon.
                </p>
              </div>

            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your Name"
                      required
                      className="h-12 rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="e-mail@example.com"
                      required
                      className={`h-12 rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 ${emailError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                        }`}
                    />
                    {emailError && (
                      <p className="mt-1 text-sm text-red-500">{emailError}</p>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                    <Input
                      type="tel"
                      inputMode="numeric"
                      name="tel"
                      value={formData.tel}
                      onChange={handleChange}
                      required
                      placeholder="+1 (555) 000-0000"
                      //   className="h-12 rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                      // />
                      className={`h-12 rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 ${phoneError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                        }`}
                    />
                    {phoneError && (
                      <p className="mt-1 text-sm text-red-500">{phoneError}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="h-12 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:ring-emerald-500 focus:outline-none"
                    >
                      <option value="">Select a topic</option>
                      <option value="General Inquiry">General Inquiry</option>
                      <option value="Restorative Programs">Restorative Programs</option>
                      <option value="Restorative Nursing Care">Restorative Nursing Care</option>
                      <option value="Community Events">Community Events</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Message *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us how we can help you..."
                    required
                    rows={5}
                    className="rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting || !formData.name || !formData.email || !formData.subject || !formData.message || emailError || phoneError}
                  className="w-full h-14 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold rounded-xl shadow-lg shadow-emerald-500/30 transition-all duration-300"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            )}
          </motion.div>


          {/* Map & Contact Info */}
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
          {/* Google Map */}
            <div className="rounded-3xl overflow-hidden shadow-xl h-64 lg:h-72">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2699.681810823217!2d-79.30320358847777!3d43.8006552709749!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89d4d3dd25ab8863%3A0xcb2f01c46891187b!2s3850%20Finch%20Ave%20E%20%23306%2C%20Scarborough%2C%20ON%20M1T%203T6!5e1!3m2!1sen!2sca!4v1762561426617!5m2!1sen!2sca"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Our Location"
              />
            </div>

            {/* Contact Info Cards */}
             {/* <div className="grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-2  gap-2">  */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              {contactInfo.map((info, index) => (
                <div
                  key={info.label}
                  className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md hover:border-emerald-100 transition-all duration-300"
                >
                  <div className="flex items-start gap-1">
                    <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100">
                      <info.icon className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">{info.label}</p>
                      <p className="text-xs font-sm text-gray-900">{info.value}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div> 

          </motion.div>
        </div>
      </div>

    </section>
  );
}